import axios from '../request'
import type { BaseResponse } from '../types'

// 消息类型定义
export interface ChatMessage {
  messageId: number;
  historyId: number;
  assistantId: number;
  messageIndex: number;
  userId: number;
  classId: number;
  courseId: number;
  role: 'user' | 'assistant';
  content: string;
  star: number;
  readFlag: boolean;
  tag: string | null;
  sessionId: string;
  sessionStartReason: string | null;
  creationDate: string;
  lastUpdateDate: string;
  graph?: any;  // 知识图谱信息
  files?: any[];  // 课程资源文件列表
}

// 知识点类型定义
export interface KnowledgePoint {
  elementId: string;
  name: string;
  alias: string;
}

// 知识点关系类型定义
export interface KnowledgeRelation {
  elementId: string;
  startNodeElementId: string;
  endNodeElementId: string;
  names: string[];
}

// 图谱信息类型定义
export interface GraphInfo {
  type: string;
  kps: KnowledgePoint[];
  kpRels: KnowledgeRelation[];
  graphPic: string;
  empty: boolean;
}

// 课程助手类型定义
export interface CourseAssistant {
  id: number;
  name: string;
  photo: string;
  type: string;
  roleDesc: string;
  courseId: number;
  courseName: string;
  creatorId: number;
  creatorName: string;
}

/**
 * 标记全部消息为已读（所有助教）
 * @returns 接口响应
 */
export function markAllMessagesAsRead() {
  return axios.get<BaseResponse<void>>('/student-assistant/set-all-read')
}

/**
 * 标记指定助教的全部消息为已读
 * @param classId 班级ID
 * @param assistantId 助教ID
 * @returns 接口响应
 */
export function markAssistantMessagesAsRead(classId: number | string, assistantId: number | string) {
  return axios.get<BaseResponse<void>>(`/student-assistant/set-all-read/${classId}/${assistantId}`)
}

/**
 * 获取猜你想问列表
 * @param classId 班级ID
 * @param assistantId 助教ID
 * @returns 猜你想问列表
 */
export function getGuessQuestionsList(classId: number | string, assistantId: number | string) {
  return axios.get<BaseResponse<string[]>>(`/student-assistant/search-latest-associations/${classId}/${assistantId}`)
}

/**
 * 倒序查询学生AI助手对话消息
 * @param classId 班级ID
 * @param assistantId 助教ID
 * @param size 一次查询数据量，必填
 * @param startMessageIndex 数据起始索引(不包含)，可选
 * @returns 对话消息列表
 */
export function getHistoryMessages(
  classId: number | string,
  assistantId: number | string,
  size: number = 50,
  startMessageIndex?: number
) {
  const params: any = { size }
  if (startMessageIndex !== undefined) {
    params.startMessageIndex = startMessageIndex
  }
  return axios.get<BaseResponse<ChatMessage[]>>(
    `/student-assistant/search-history-messages/${classId}/${assistantId}`,
    { params }
  )
}

/**
 * 获取全部基础助教未读数量
 * @returns 未读数量
 */
export function getUnreadCount() {
  return axios.get<BaseResponse<number>>('/student-assistant/count-all-basic-not-read')
}

/**
 * 评价消息
 * @param messageId 消息ID
 * @param mark 评分：1点赞，-1点踩，0不赞不踩
 * @returns 接口响应
 */
export function evaluateMessage(messageId: number | string, mark: number) {
  return axios.get<BaseResponse<void>>(`/student-assistant/evaluate/${messageId}/${mark}`)
}

/**
 * 获取当前学期课程助手列表
 * @returns 课程助手列表
 */
export function getCurrentTermAssistants() {
  return axios.get<BaseResponse<CourseAssistant[]>>('/student-assistant/current-term/list')
}

/**
 * 语言选项接口
 */
export interface LanguageOption {
  code: string
  name: string
}

/**
 * 助教对话设置类型定义
 */
export interface AssistantChatSetting {
  lastTaMode: string           // 上一次选择的助教模式
  lastChatStyle: string        // 上一次选择的对话风格
  lastCelebrity: string        // 上一次填写的明星
  lastLangCode: string         // 上一次选择的语言编码
  lastLangName: string         // 上一次选择的语言名称
  langCodes: string[]          // 可选择的语言编码列表
  langNames: string[]          // 可选择的语言名称列表
}

/**
 * 资源库信息
 */
export interface LibraryInfo {
  libraryId: number
  name: string
  description: string
  type: string
}

/**
 * AI助教详细设置
 */
export interface AssistantDetailSetting {
  id: number
  courseId: number
  name: string
  photo: string
  openingStatement: string
  type: string
  roleDesc: string | null
  ability: string | null
  goal: string | null
  retrievalList: string[]
  retrievalDescList: string[]
  chatMode: string | null
  chatModeDesc: string | null
  share: boolean
  creatorId: number
  creationDate: string
  updaterId: number
  lastUpdateDate: string
  versionNum: number
  libraryList: LibraryInfo[]
  taModeList: string | string[]           // 助教模式编码列表（字符串或数组）
  taModeDescList: string | string[]       // 助教模式描述列表（字符串或数组）
  chatStyleList: string | string[]        // 语言风格编码列表（字符串或数组）
  chatStyleDescList: string | string[]    // 语言风格描述列表（字符串或数组）
  useGalaxy: boolean
  galaxyIdList: number[]
}

/**
 * 获取助教所属班级的语种集合
 * @param classId 班级ID
 * @returns 语种集合
 */
export function getAssistantLanguages(classId: number | string) {
  return axios.get<BaseResponse<LanguageOption[]>>(`/student-assistant/languages/${classId}`)
}

/**
 * 获取回答的关联图谱信息
 * @param messageId 消息ID
 * @returns 图谱信息
 */
export function getAnswerRelatedGraph(messageId: number | string) {
  return axios.get<BaseResponse<GraphInfo>>(`/student-assistant/answer-rel-graph/${messageId}`)
}

/**
 * 获取用户AI助教当前对话设置值
 * @param assistantId 助教ID
 * @returns 对话设置信息
 */
export function getUserChatSetting(assistantId: number | string) {
  return axios.get<BaseResponse<AssistantChatSetting>>(`/assistant/user-current-chat-setting/${assistantId}`)
}

/**
 * 保存用户AI助教当前对话设置值
 * @param assistantId 助教ID
 * @param settings 设置参数
 * @returns 接口响应
 */
export function saveUserChatSetting(
  assistantId: number | string,
  settings: {
    lastTaMode: string
    lastChatStyle: string
    lastCelebrity: string
    lastLangCode: string
  }
) {
  return axios.post<BaseResponse<void>>(`/assistant/user-current-chat-setting/${assistantId}`, settings)
}

/**
 * 查询AI助教设置明细
 * @param aiAssistantId AI助教的ID
 * @returns AI助教详细设置
 */
export function getAssistantDetailSetting(aiAssistantId: number | string) {
  return axios.get<BaseResponse<AssistantDetailSetting>>(`/assistant/detail-setting/${aiAssistantId}`)
}
